import { Form } from 'src/pages/Materials'
import { Formik, FormikErrors, FormikValues, useFormik } from 'formik'
import { requireField } from 'src/constants/constants'
import { filter, isEmpty, map } from 'lodash'
import { validateUrl } from 'src/utils/validateUrl'
import { _notify, NotificationTypes } from 'src/utils/notify'
import { useGetMaterialQuery, useUpdateMaterialMutation } from 'src/api/endpoints/materials'
import useAppSelector from 'src/hooks/useAppSelector'
import React, { FC, ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { FileUpload, FileUploadType } from 'src/components/FileUpload/FileUpload'
import { Dropdown as AppDropDown } from 'src/components/Dropdown'
import { classNames } from 'primereact/utils'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import {
    generateFiles,
    serializeFiles,
} from 'src/pages/Materials/components/MaterialEditingPopup/Content/MaterialsEditingContent.functions'
import { FileUploadRemoveEvent, FileUploadSelectEvent } from 'primereact/fileupload'
import { skipToken } from '@reduxjs/toolkit/query'
import { InputNumber } from 'primereact/inputnumber'
import { useGetSubjectsQuery } from 'src/api/endpoints/subjects/api'
import { ResponseError } from 'src/api'
import { Nullable } from 'primereact/ts-helpers'
import {
    useGetMaterialHomeworkFilesQuery,
    useGetMaterialLessonFilesQuery,
    useGetMaterialPracticeFilesQuery,
    useGetMaterialPresentationFilesQuery,
} from 'src/api/endpoints/materials/files/api'

export const isFormFieldValid = <T extends FormikValues>(formik: Formik<T>, name: keyof T): boolean =>
    !!(formik.touched[name] && formik.errors[name])

export const getFormErrorMessage = <T extends FormikValues>(formik: Formik<T>, name: keyof T): ReactNode => {
    return (
        isFormFieldValid(formik, name) && (
            <small className="p-error">{formik.errors[name]?.toString?.()}</small>
        )
    )
}

export type MaterialEditingPopupContentProps = {
    id: number | null;
    subjectId?: Nullable<number>;
    onSubmit: (values: Form) => void;
    onValid: () => void;
};

export const MaterialEditingPopupContent: FC<MaterialEditingPopupContentProps> = (
    props: MaterialEditingPopupContentProps,
): ReactElement => {
    const { id, subjectId, onSubmit, onValid } = props
    
    const formik: Formik<Partial<Form>> = useFormik<Partial<Form>>({
        initialValues: {
            index: 0,
            subjectId: (id == null ? subjectId : undefined) ?? undefined,
            materialFiles: [],
            homeworkFiles: [],
            practiceFiles: [],
            presentationFiles: [],
        },
        validate: ({ subjectId, name, materialFiles, materialLink, index }: Partial<Form>): FormikErrors<Form> => {
            const errors: FormikErrors<Form> = {}
            
            if (!subjectId) errors.subjectId = requireField
            if (!name) errors.name = requireField
            
            if (index === undefined)
                errors.index = requireField
            else if (index <= 0) {
                errors.index = 'Номер занятия должен быть больше нуля'
            }
            
            if (!materialLink?.trim().length && isEmpty(materialFiles)) {
                errors.materialLink = 'Введите ссылку или выберите файл'
                errors.materialFiles = 'Введите ссылку или выберите файл'
            } else {
                if (materialLink && !validateUrl(materialLink)) {
                    errors.materialLink = 'Некорректная ссылка'
                }
            }
            
            return errors
        },
        onSubmit: (values: Partial<Form>): void => {
            const formResult: Form = values as Form
            
            if (!id) return onSubmit(formResult)
            
            Promise.all([
                    serializeFiles(values.materialFiles),
                    serializeFiles(values.homeworkFiles),
                    serializeFiles(values.practiceFiles),
                    serializeFiles(values.presentationFiles),
                ])
                .then(
                    ([materialFiles, homeworkFiles, practiceFiles, presentationFiles]): void => {
                        update({
                            params: {
                                subjectId: formResult.subjectId,
                                materialId: id,
                            },
                            body: {
                                name: formResult.name,
                                index: formResult.index,
                                materialLink: formResult.materialLink ?? null,
                                materialFiles,
                                homeworkFiles,
                                practiceFiles,
                                presentationFiles,
                            },
                        })
                            .unwrap()
                            .then((): void => {
                                onValid()
                                _notify(
                                    window.Toast,
                                    {
                                        type: NotificationTypes.Notification,
                                        content: 'Материал успешно обновлён!',
                                    },
                                    true,
                                )
                            })
                    },
                )
                .catch((error: ResponseError): void => {
                    _notify(
                        window.Toast,
                        {
                            type: NotificationTypes.Exception,
                            content: error.message,
                        },
                        true,
                    )
                })
        },
    })
    
    const { data: material, isLoading: isMaterial } = useGetMaterialQuery(
        id
        ? { params: { materialId: id } }
        : skipToken,
    )
    
    // region Lesson Files Loading
    const lessonFileUploaderRef = useRef<FileUploadType>(null)
    const [isLessonFilesInitialized, setLessonFilesInitialized] = useState(false)
    const { data: lessonFiles, isLoading: isLessonFilesLoading } = useGetMaterialLessonFilesQuery(
        material ? {
            params: {
                subjectId: material.subject.id,
                materialId: material.id,
            },
        } : skipToken,
    )
    
    useEffect(() => {
        if (isLessonFilesInitialized) return
        if (!lessonFileUploaderRef.current) return
        if (isLessonFilesLoading) return
        if (!lessonFiles) return
        
        const parsedLessonFiles: File[] = generateFiles(lessonFiles)
        
        formik.setFieldValue('materialFiles', parsedLessonFiles)
        lessonFileUploaderRef.current.setFiles(parsedLessonFiles)
        
        setLessonFilesInitialized(true)
    }, [isLessonFilesLoading, lessonFiles, isLessonFilesInitialized, lessonFileUploaderRef.current])
    // endregion
    
    // region Homework Files Loading
    const homeworkFileUploaderRef = useRef<FileUploadType>(null)
    const [isHomeworkFilesInitialized, setHomeworkFilesInitialized] = useState(false)
    const { data: homeworkFiles, isLoading: isHomeworkFilesLoading } = useGetMaterialHomeworkFilesQuery(
        material ? {
            params: {
                subjectId: material.subject.id,
                materialId: material.id,
            },
        } : skipToken,
    )
    
    useEffect(() => {
        if (isHomeworkFilesInitialized) return
        if (!homeworkFileUploaderRef.current) return
        if (isHomeworkFilesLoading) return
        if (!homeworkFiles) return
        
        const parsedHomeworkFiles: File[] = generateFiles(homeworkFiles)
        
        formik.setFieldValue('homeworkFiles', parsedHomeworkFiles)
        homeworkFileUploaderRef.current.setFiles(parsedHomeworkFiles)
        
        setHomeworkFilesInitialized(true)
    }, [isHomeworkFilesLoading, homeworkFiles, isHomeworkFilesInitialized, homeworkFileUploaderRef.current])
    // endregion
    
    // region Practice Files Loading
    const practiceFileUploaderRef = useRef<FileUploadType>(null)
    const [isPracticeFilesInitialized, setPracticeFilesInitialized] = useState(false)
    const { data: practiceFiles, isLoading: isPracticeFilesLoading } = useGetMaterialPracticeFilesQuery(
        material ? {
            params: {
                subjectId: material.subject.id,
                materialId: material.id,
            },
        } : skipToken,
    )
    
    useEffect(() => {
        if (isPracticeFilesInitialized) return
        if (!practiceFileUploaderRef.current) return
        if (isPracticeFilesLoading) return
        if (!practiceFiles) return
        
        const parsedPracticeFiles: File[] = generateFiles(practiceFiles)
        
        formik.setFieldValue('practiceFiles', parsedPracticeFiles)
        practiceFileUploaderRef.current.setFiles(parsedPracticeFiles)
        
        setPracticeFilesInitialized(true)
    }, [isPracticeFilesLoading, practiceFiles, isPracticeFilesInitialized, practiceFileUploaderRef.current])
    // endregion
    
    // region Presentation Files Loading
    const presentationFileUploaderRef = useRef<FileUploadType>(null)
    const [isPresentationFilesInitialized, setPresentationFilesInitialized] = useState(false)
    const { data: presentationFiles, isLoading: isPresentationFilesLoading } = useGetMaterialPresentationFilesQuery(
        material ? {
            params: {
                subjectId: material.subject.id,
                materialId: material.id,
            },
        } : skipToken,
    )
    
    useEffect(() => {
        if (isPresentationFilesInitialized) return
        if (!presentationFileUploaderRef.current) return
        if (isPresentationFilesLoading) return
        if (!presentationFiles) return
        
        const parsedPresentationFiles: File[] = generateFiles(presentationFiles)
        
        formik.setFieldValue('presentationFiles', parsedPresentationFiles)
        presentationFileUploaderRef.current.setFiles(parsedPresentationFiles)
        
        setPresentationFilesInitialized(true)
    }, [isPresentationFilesLoading, presentationFiles, isPresentationFilesInitialized, presentationFileUploaderRef.current])
    // endregion
    
    const { data: subjects, isLoading: isSubjectsLoading } = useGetSubjectsQuery()
    
    const { pending } = useAppSelector(
        state => state.adminMaterialReducer.createMaterial,
    )
    
    const [update, { isLoading }] = useUpdateMaterialMutation()
    
    useEffect((): void => {
        if (isMaterial) return
        if (!material) return
        
        formik.setFieldValue('name', material.name)
        formik.setFieldValue('index', material.index)
        formik.setFieldValue('subjectId', material.subject.id)
        formik.setFieldValue('materialLink', material.sourceLink)
    }, [
        isMaterial,
        material,
    ])
    
    return (
        <div>
            <form onSubmit={formik.handleSubmit} className="p-fluid">
                <div className="field">
                    <span className="p-float-label">
                        <AppDropDown
                            filter
                            disabled={id !== null}
                            id="subjectId"
                            name="subjectId"
                            options={map(subjects, ({ name, id }) => ({
                                label: name,
                                value: id,
                            }))}
                            loading={isSubjectsLoading}
                            value={formik.values.subjectId}
                            onChange={formik.handleChange}
                            className={classNames({
                                'p-invalid': isFormFieldValid(formik, 'subjectId'),
                            })}
                        />
                        <label
                            htmlFor="subjectId"
                            className={classNames({
                                'p-error': isFormFieldValid(formik, 'subjectId'),
                            })}
                        >
                            Предмет
                        </label>
                    </span>
                    {getFormErrorMessage(formik, 'subjectId')}
                </div>
                <div className="field">
                    <span className="p-float-label">
                        <InputText
                            id="name"
                            name="name"
                            value={formik.values.name}
                            onInput={formik.handleChange}
                            className={classNames({
                                'p-invalid': isFormFieldValid(formik, 'name'),
                            })}
                        />
                        <label
                            htmlFor="name"
                            className={classNames({
                                'p-error': isFormFieldValid(formik, 'name'),
                            })}
                        >
                            Наименование
                        </label>
                    </span>
                    {getFormErrorMessage(formik, 'name')}
                </div>
                <div className="field">
                    <span className="p-float-label">
                        <InputNumber
                            id="index"
                            name="index"
                            value={formik.values.index}
                            onChange={(event) => {
                                formik.setFieldValue('index', event.value ?? undefined)
                            }}
                            className={classNames({
                                'p-invalid': isFormFieldValid(formik, 'index'),
                            })}
                        />
                        <label
                            htmlFor="index"
                            className={classNames({
                                'p-error': isFormFieldValid(formik, 'index'),
                            })}
                        >
                            Номер занятия
                        </label>
                    </span>
                    {getFormErrorMessage(formik, 'index')}
                </div>
                <small className="date">Ссылка на внешний источник</small>
                <div className="field mt-8">
                    <InputText
                        id="materialLink"
                        name="materialLink"
                        className={classNames({
                            'p-invalid': isFormFieldValid(formik, 'materialLink'),
                        })}
                        onChange={formik.handleChange}
                        placeholder="https://example.com/"
                        value={formik.values.materialLink}
                    />
                    {getFormErrorMessage(formik, 'materialLink')}
                </div>
                <small className="date">Файлы занятия</small>
                <div className="field mt-8">
                    <FileUpload
                        ref={lessonFileUploaderRef}
                        progressBarTemplate={<></>}
                        onSelect={(event: FileUploadSelectEvent): void => {
                            formik.setFieldValue('materialFiles', [...(formik.values.materialFiles ?? []), ...event.files])
                        }}
                        onRemove={(event: FileUploadRemoveEvent): void => {
                            formik.setFieldValue(
                                'materialFiles',
                                filter(
                                    formik.values.materialFiles,
                                    (file: File): boolean => file.name !== event.file.name,
                                ),
                            )
                        }}
                    />
                    {getFormErrorMessage(formik, 'materialFiles')}
                </div>
                <small className={'date'}>Файлы домашнего задания</small>
                <div className="field mt-8">
                    <FileUpload
                        ref={homeworkFileUploaderRef}
                        progressBarTemplate={<></>}
                        onSelect={event =>
                            formik.setFieldValue('homeworkFiles', [...(formik.values.homeworkFiles ?? []), ...event.files])
                        }
                        onRemove={e =>
                            formik.setFieldValue(
                                'homeworkFiles',
                                filter(
                                    formik.values.homeworkFiles,
                                    _file => _file.name !== e.file.name,
                                ),
                            )
                        }
                    />
                    {getFormErrorMessage(formik, 'homeworkFiles')}
                </div>
                <small className={'date'}>Файлы презентации</small>
                <div className="field mt-8">
                    <FileUpload
                        ref={presentationFileUploaderRef}
                        progressBarTemplate={<></>}
                        onSelect={event =>
                            formik.setFieldValue('presentationFiles', [...(formik.values.presentationFiles ?? []), ...event.files])
                        }
                        onRemove={e =>
                            formik.setFieldValue(
                                'presentationFiles',
                                filter(
                                    formik.values.presentationFiles,
                                    _file => _file.name !== e.file.name,
                                ),
                            )
                        }
                    />
                    {getFormErrorMessage(formik, 'presentationFiles')}
                </div>
                <small className={'date'}>Файлы практической работы</small>
                <div className="field mt-8">
                    <FileUpload
                        ref={practiceFileUploaderRef}
                        progressBarTemplate={<></>}
                        onSelect={event =>
                            formik.setFieldValue('practiceFiles', [...(formik.values.practiceFiles ?? []), ...event.files])
                        }
                        onRemove={e =>
                            formik.setFieldValue(
                                'practiceFiles',
                                filter(
                                    formik.values.practiceFiles,
                                    _file => _file.name !== e.file.name,
                                ),
                            )
                        }
                    />
                    {getFormErrorMessage(formik, 'practiceFiles')}
                </div>
                <Button
                    loading={id ? isLoading : pending}
                    type="submit"
                    label="Сохранить"
                    className={'w-auto'}
                />
            </form>
        </div>
    )
}